import { cn } from "@/utils";
import { Search, X } from "lucide-react";
import { useState } from "react";
import { WIDGET_PRESETS, type WidgetPreset } from "../nodes/widgets";

// Approximate rendered column width inside a 480px, 2-col, p-2 grid.
// Used to compute per-preset scale so each widget fills as much of the
// preview area as possible without distorting its design proportions.
const PREVIEW_W = 210;
const PREVIEW_H = 96;

/**
 * Compute scale so the widget fills ~75 % of the preview area in the
 * tightest dimension. Capped at 1.5× to avoid blurry upscaling.
 *
 * This means a 44 px-tall pill badge (Hot Take) gets scaled up to fill
 * the height, while a 280×280 Earth & Moon scene is scaled down to fit.
 */
function presetScale(preset: WidgetPreset): number {
  if (preset.previewScale !== undefined) return preset.previewScale;
  const scaleToFit = Math.min(
    (PREVIEW_W * 0.75) / preset.defaultWidth,
    (PREVIEW_H * 0.75) / preset.defaultHeight,
  );
  return Math.min(scaleToFit, 1.5);
}

interface CanvasWidgetsPopoverProps {
  onAddWidget: (preset: WidgetPreset) => void;
}

export function CanvasWidgetsPopover({
  onAddWidget,
}: CanvasWidgetsPopoverProps) {
  const [searchQuery, setSearchQuery] = useState("");
  // Internal presets are registered for lookup/rendering but must never be
  // user-placeable — exclude them from the picker entirely.
  const placeablePresets = WIDGET_PRESETS.filter((p) => !p.internal);
  const filteredPresets = searchQuery
    ? placeablePresets.filter((p) =>
        p.label.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : placeablePresets;

  return (
    <div
      className="z-20 w-[480px] max-w-[calc(100vw-2rem)]"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={cn(
          "bg-neutral-0/95 backdrop-blur-lg",
          "border rounded-2xl",
          "shadow-2xl shadow-black/20",
          // Fixed height — popover never shrinks when search filters results.
          // h-[420px] matches other toolbar popovers.
          "h-[460px] flex flex-col overflow-hidden",
        )}
      >
        {/* Search bar */}
        <div className="flex items-center border-b px-3 gap-2 shrink-0">
          <Search className="h-4 w-4 shrink-0 opacity-50" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
            className="flex h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="shrink-0">
              <X className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" />
            </button>
          )}
        </div>

        {/* Scrollable grid — flex-1 + min-h-0 so it respects the fixed outer height */}
        <div className="flex-1 min-h-0 overflow-y-auto p-2">
          {filteredPresets.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-muted-foreground">No widgets found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {filteredPresets.map((preset) => (
                <WidgetPreviewCard
                  key={preset.id}
                  preset={preset}
                  onSelect={() => {
                    // Only start placement — do NOT call onClose() here.
                    // The popover stays open so the user can place more widgets
                    // (identical to ShapesPopover which never closes on selection).
                    onAddWidget(preset);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface WidgetPreviewCardProps {
  preset: WidgetPreset;
  onSelect: () => void;
}

function WidgetPreviewCard({ preset, onSelect }: WidgetPreviewCardProps) {
  const scale = presetScale(preset);

  return (
    <button
      onClick={onSelect}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("application/widget-preset", preset.id);
        e.dataTransfer.effectAllowed = "move";

        // Replace the default drag ghost (the whole card) with just the
        // widget preview content, scaled to a readable size.
        const ghost = document.createElement("div");
        ghost.style.cssText = `
          position: fixed; top: -9999px; left: -9999px;
          width: ${preset.defaultWidth}px;
          transform: scale(${Math.min(0.5, 200 / preset.defaultWidth)});
          transform-origin: top left;
          opacity: 0.9;
          pointer-events: none;
        `;
        ghost.className = "widget-anim-played";
        ghost.innerHTML = preset.previewHtml ?? preset.html ?? "";
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, 0, 0);
        requestAnimationFrame(() => document.body.removeChild(ghost));
      }}
      className={cn(
        "group rounded-xl overflow-hidden w-full text-left",
        "border hover:border-primary/40",
        "bg-muted/20 hover:bg-primary/[0.04]",
        "hover:shadow-md",
        "transition-all duration-150 cursor-grab active:cursor-grabbing",
      )}
    >
      {/* Preview area — canvas background so the widget looks exactly as it
          will on canvas. Overflow is hidden so upscaled widgets don't bleed. */}
      <div
        className="w-full relative overflow-hidden bg-neutral-50 dark:bg-neutral-800 flex items-center justify-center"
        style={{ height: PREVIEW_H }}
      >
        <div
          className="widget-anim-played"
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "center center",
            width: preset.defaultWidth,
            height: preset.defaultHeight,
            flexShrink: 0,
            pointerEvents: "none",
            // Center the widget's inner content regardless of its display type.
            // Widgets using display:inline-flex or inline-block would otherwise
            // be left-aligned within the container, visually off-center after scaling.
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: preset.previewHtml ?? preset.html ?? "",
          }}
        />
      </div>

      {/* Label */}
      <div className="px-2.5 py-2">
        <span className="block text-[11px] font-medium text-muted-foreground group-hover:text-foreground/80 transition-colors truncate">
          {preset.label}
        </span>
      </div>
    </button>
  );
}
